import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import { useAuth } from "../../context/AuthContext";
import { registerSchema } from "./validations/auth.schema";
import { getAuthErrorKey } from "../../utils/authErrors";
import {
  authCardClass,
  authInputClass,
  authInputErrorClass,
  authLabelClass,
  authLinkClass,
} from "./authTheme";

type RegisterFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
};

const AUTH_ERROR_KEYS: Record<string, string> = {
  "auth.userExists": "register.errors.userExists",
  "auth.invalidCredentials": "register.errors.generic",
};

const Register = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const location = useLocation();
  const { register: registerUser, isAuthenticated } = useAuth();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    "/profile";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(registerSchema),
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const onSubmit = async (data: RegisterFormData) => {
    setError("");
    setIsLoading(true);

    try {
      await registerUser({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
      });
      navigate(from, { replace: true });
    } catch (err) {
      const errorKey = getAuthErrorKey(err, "auth.register.errors.generic");
      const i18nKey = AUTH_ERROR_KEYS[errorKey] || errorKey;
      setError(t(i18nKey));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout title={t("create_account") || "Create Account"}>
      <div className="flex min-h-[70vh] items-center justify-center">
        <div className={authCardClass}>
          <div className="text-center mb-8">
            <h2 className="text-h2 text-white">
              {t("create_account") || "Create Account"}
            </h2>
            <p className="mt-3 text-body-sm text-gray-400">
              {from === "/checkout" && (
                <span className="block mb-2 text-neon-green">
                  註冊後即可返回結帳。
                </span>
              )}
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-body-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className={authLabelClass}>
                  {t("first_name") || "First Name"}
                </label>
                <input
                  type="text"
                  id="firstName"
                  {...register("firstName")}
                  className={`${authInputClass} ${
                    errors.firstName ? authInputErrorClass : ""
                  }`}
                />
                {errors.firstName && (
                  <p className="text-red-400 text-caption mt-1">
                    {t(errors.firstName.message as string)}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className={authLabelClass}>
                  {t("last_name") || "Last Name"}
                </label>
                <input
                  type="text"
                  id="lastName"
                  {...register("lastName")}
                  className={`${authInputClass} ${
                    errors.lastName ? authInputErrorClass : ""
                  }`}
                />
                {errors.lastName && (
                  <p className="text-red-400 text-caption mt-1">
                    {t(errors.lastName.message as string)}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className={authLabelClass}>
                {t("email") || "Email Address"}
              </label>
              <input
                type="email"
                id="email"
                autoComplete="email"
                {...register("email")}
                className={`${authInputClass} ${
                  errors.email ? authInputErrorClass : ""
                }`}
              />
              {errors.email && (
                <p className="text-red-400 text-caption mt-1">
                  {t(errors.email.message as string)}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className={authLabelClass}>
                {t("password") || "Password"}
              </label>
              <input
                type="password"
                id="password"
                autoComplete="new-password"
                {...register("password")}
                className={`${authInputClass} ${
                  errors.password ? authInputErrorClass : ""
                }`}
              />
              {errors.password && (
                <p className="text-red-400 text-caption mt-1">
                  {t(errors.password.message as string)}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className={authLabelClass}>
                {t("confirm_password") || "Confirm Password"}
              </label>
              <input
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                {...register("confirmPassword")}
                className={`${authInputClass} ${
                  errors.confirmPassword ? authInputErrorClass : ""
                }`}
              />
              {errors.confirmPassword && (
                <p className="text-red-400 text-caption mt-1">
                  {t(errors.confirmPassword.message as string)}
                </p>
              )}
            </div>

            <Button
              type="submit"
              size="lg"
              isLoading={isLoading}
              className="w-full mt-2 bg-gradient-brand text-space hover:opacity-90"
            >
              {isLoading
                ? t("register.signingUp") || "Signing up..."
                : t("register_button") || "Sign Up"}
            </Button>
          </form>

          <p className="mt-6 text-center text-body-sm text-gray-400">
            {t("already_have_account") || "Already have an account?"}{" "}
            <Link to="/login" state={location.state} className={authLinkClass}>
              {t("login_here") || "Login here"}
            </Link>
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Register;
